<template>
    <section class="select-a-dealer" id="selectADealer2">
        <div class="select-a-dealer__container container">
            <StepBar />

            <StepLink :step="shortOnlineServiceBooking ? 4 : 3" />

            <template v-if="submitting">
                <LoadingSpinner />
            </template>
            <template v-else>
                <h3 class="title select-date-time__title">Select a date & time</h3>
                <p class="select-date-time__heading-text">Now you’ve selected {{ selectedDealer.name }}, please select date and time you’d like to drop your vehicle off.</p>

                <div class="select-date-time__container">
                    <div class="select-date-time__dates-and-dealer">
                        <div class="card select-a-dealer__drop-off-date-card">
                            <div class="card__inner select-a-dealer__card-flex">
                                <div class="date-time__container">
                                    <div class="date-time__top">
                                        <div class="flatpickr">
                                            <input
                                                type="text"
                                                name="date"
                                                ref="date"
                                                id="date"
                                                class="form-control form-control--select select-a-dealer__select-input select-date-time__select-input"
                                                :value="formattedDate"
                                                placeholder="Please select a date"
                                            />
                                            <button class="input-button" @click="toggleDateSelect"></button>
                                        </div>

                                        <div class="select-a-dealer__desktop-dealer-info">
                                            <h3 class="card__heading">Dealer Address</h3>
                                            <p class="card__text">{{ selectedDealer.name }} <br /></p>
                                            <p class="card__text">
                                                {{ selectedDealer.address }} <br />
                                                {{ selectedDealer.postcode }} <br />
                                            </p>
                                            <br />
                                            <!--  <p class="card__text"><strong>Hours:</strong> close soon 6pm, opens 9AM Wed</p>-->
                                            <br />
                                            <!--
                                    <p class="card__text">
                                        Contact us on: <br />
                                        <span class="select-a-dealer__card-phone-number">{{ selectedDealer.tel_general }}</span> <br />
                                    </p>
                                    -->
                                        </div>
                                    </div>

                                    <div v-if="timesForSelectedDate && timesForSelectedDate !== 'closed'" class="date-time__bottom">
                                        <p class="date-time__times-title">Please select a timeslot:</p>
                                        <div class="date-time__times-container">
                                            <div v-for="(time, index) in timesForSelectedDate" :key="index" class="date-time__times-tile" :class="{ selected: selectedTime === time }" @click="setTime(time)">
                                                <input type="radio" :id="time.split(' ').join('')" name="bookingTime" :value="time.split(' ').join('')" class="select-a-dealer__radio-input" />
                                                <label :for="time.split(' ').join('')" class="find-my-car__time-label"> {{ time }} </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="timesForSelectedDate == 'closed'" class="select-date-time__dealer-closed">This dealership is closed on this day, please select another day.</div>
                                    <button type="button" class="btn btn--primary btn--full-width card__button select-date-time__mobile-button" @click.prevent="next_step">
                                        <span class="find-my-car__used-btn-txt">Continue</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p v-if="validationMessage" class="select-date-time__validation-error">{{ validationMessage }}</p>

                        <button type="button" class="btn btn--primary btn--full-width card__button select-date-time__desktop-button" @click.prevent="next_step">
                            <span class="find-my-car__used-btn-txt">{{ shortOnlineServiceBooking ? 'Confirm Booking' : 'Continue' }}</span>
                        </button>
                    </div>

                    <BookingOverview />
                </div>
            </template>
        </div>
    </section>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex/dist/vuex.esm.browser";
import { BookingOverview, StepBar, StepLink } from "../";
import EventObject from "../../../modules/tracking/EventObject";
import LoadingSpinner from "../../components/LoadingSpinner.vue";
import { isSameDay, format, parse, setHours } from "date-fns";

export default {
    components: {
        BookingOverview,
        LoadingSpinner,
        StepBar,
        StepLink,
    },
    props: {
        submitting: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            minDate: null,
            maxDate: null,
            isMounted: false,
            validationMessage: false,
            flatpickrInstance: false,
            dealerTimes: false,
            closedDays: [],
            timesForSelectedDate: null,
        };
    },

    computed: {
        ...mapState("Dealers", ["selectedDealer"]),
        ...mapState("OSB", ["selectedDate", "selectedTime", "selectedVehicle", "otherSelected", "nonFranchiseVehicle", "shortOnlineServiceBooking"]),

        formattedDate() {
            return this.selectedDate ? new Date(this.selectedDate).toDateString() : "";
        },

        directionsLink() {
            return this.selectedDealer.address ? `https://www.google.com/maps?daddr=${this.selectedDealer.address}+${this.selectedDealer.town}+${this.selectedDealer.postcode}` : "#";
        },

        /*
        * Function to provide times at 15 minute intervals rather than 30 min provided by
        * dealer-opening-times api
        * */
        // hardcodedDealerTimes() {
        //     return hardCodedTimes();
        // },
    },

    watch: {
        selectedTime(val) {
            if (val && this.selectedDate) {
                this.validationMessage = false;
            }
        },
    },

    methods: {
        ...mapMutations("OSB", ["incrementStep", "setSelectedDate", "setSelectedTime"]),
        ...mapActions("OSB", ["throwFatalError"]),

        getDealerClosedDays() {
            return new Promise((resolve, reject) => {
                axios({
                    method: "GET",
                    url: "/api/dealer-closed-days?dealer=" + this.selectedDealer.id + "&service_type=Service",
                })
                .then((response) => {
                    const closedDays = response.data;
                    closedDays.forEach((closedDay, index) => {
                        closedDays[index] = parse(closedDay);
                    })
                    this.closedDays = closedDays;
                    resolve(closedDays);
                })
                .catch((error) => {
                    this.throwFatalError();
                    reject();
                });
            });
        },

        getFirstBookableDate() {
            return new Promise((resolve, reject) => {
                axios({
                    method: "GET",
                    url: "/api/dealer-first-bookable-date",
                    params: {
                        location: this.selectedDealer.id,
                        service_type: 'service',
                        lead_time: 'osb',
                    },
                })
                    .then(response => {
                        this.minDate = new Date(Date.parse(response.data));
                        resolve();
                        // this.initializeDatePicker();
                    })
                    .catch(error => {
                        console.error("error", error);
                        this.minDate = new Date().fp_incr(2);
                        resolve();
                        // this.initializeDatePicker();
                    });
            })
        },

        getAllClosedDays() {
            return new Promise((resolve, reject) => {
                Promise.all([
                    this.getDealerClosedDays(),
                    this.getFirstBookableDate()
                ])
                .then(() => {
                    resolve(this.closedDays);
                })
                .catch(error => {
                    console.error(error);
                    reject(error);
                });
            })
        },

        initializeDatePicker() {
            if (this.flatpickrInstance !== false) {
                return;
            }
            this.flatpickrInstance = flatpickr(this.$refs.date, {
                minDate: this.minDate,
                maxDate: this.maxDate,
                altInput: false,
                dateFormat: "D J F Y",
                locale: {
                    firstDayOfWeek: 1, // start week on Monday
                },
                disable: [
                    date => {
                        const closedDaysLength = this.closedDays.length;
                        if (closedDaysLength == 0) {
                            return false;
                        }

                        for (let index = 0; index < closedDaysLength; index++) {
                            if (isSameDay(this.closedDays[index], date)) {
                                return true;
                            }
                        }

                        return false;
                    },
                ],

                onChange: ([selectedDate]) => {
                    this.setSelectedDate(setHours(selectedDate, 12));
                    this.getDealerOpeningTimes();
                },
            });
        },

        toggleDateSelect() {
            this.flatpickrInstance.open();
        },

        getDealerOpeningTimes() {
            axios.get('/api/dealer-opening-times', {
                params: {
                    dealer: this.selectedDealer.id,
                    service_type: 'Service',
                    interval: '15 minutes',
                    date: this.selectedDate,
                }
            })
                .then((response) => {
                    this.dealerTimes = response.data;
                    this.timesForSelectedDate = this.dealerTimes;

                    if (!this.isMounted) {
                        this.initializeDatePicker();
                    }
                })
                .catch((error) => {
                    this.throwFatalError();
                });
        },

        setTime(time) {
            this.setSelectedTime(time);
        },

        checkForExistingBooking() {
            axios({
                method: "POST",
                url: "/api/osb/existing-booking",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    registration: this.selectedVehicle.registration,
                    date: format(this.selectedDate, "YYYY-MM-DD HH:mm:ss"),
                    time: this.selectedTime,
                    locationId: this.selectedDealer.id,
                },
            })
                .then(response => {
                    if (response.data.record_found !== false && response.data.record_found >= 0) {
                        this.validationMessage = "A booking already exists for this vehicle with the selected dealer made within last 30 days.";
                        return;
                    }

                    if (! this.shortOnlineServiceBooking) {  
                        this.incrementStep();
                    }
                    this.scrollToTop();
                })
                .catch(error => {
                    if (! this.shortOnlineServiceBooking) {
                        this.incrementStep();
                    }
                    this.scrollToTop();
                    console.error(error);
                    // this.setError(true);
                    return false;
                });
        },

        scrollToTop() {
            window.scrollTo(0, 0);
        },

        next_step() {
            if (!this.selectedDate || !this.selectedTime) {
                this.validationMessage = "Please select a date & time";
                return;
            }
            new EventObject("OSB", "Click", "Select date and time continue clicked").dispatch();
            this.checkForExistingBooking();
            
            this.$emit('collect-manufacturer-update');

            if (this.shortOnlineServiceBooking) {
                this.$emit('create-booking');
            }
        },
        setMaxDates(date) {
            this.minDate = date;
            this.maxDate = new Date(date);
            this.maxDate.setMonth(this.maxDate.getMonth() + 6);
        }
    },

    mounted() {
        this.isMounted = true;
        this.maxDate = new Date();
        this.maxDate.setMonth(this.maxDate.getMonth() + 6);

        this.getAllClosedDays()
            .then(() => {
                this.initializeDatePicker();
            })
            .catch(error => {
                console.error("error", error);
                this.initializeDatePicker();
            });
    },
};
</script>

<style lang="scss" scoped>
.selected {
    border-color: #0293d6;
    color: #0293d6;
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/online-service-booking/components/Steps/SelectDateTime.vue